/*
  Keeps the breadcrumb trail of snippets/breadcrumb-trail.liquid on one line. When the page's own name would get less
  than 12em (or less than its whole name, if that is shorter), the levels nearest Home fold one at a time behind the
  "…" button, until the row fits. Home, the parent and the page never fold. If even that is too wide, the page's name
  shrinks first, down to 6em, and only then the parent. The button brings every level back, on as many lines as they
  need, and moves focus to the first one it revealed.
*/

if (!customElements.get('breadcrumb-trail')) {
  class BreadcrumbTrail extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.breadcrumb-trail__list');
      this.current = this.querySelector('.breadcrumb-trail__current > [aria-current]');
      if (!this.list || !this.current) return;
      this.more = this.querySelector('.breadcrumb-trail__more');
      this.levels = [...this.querySelectorAll('.breadcrumb-trail__level')];
      this.controller = new AbortController();
      this.more?.querySelector('button')?.addEventListener('click', () => this.expand(), {
        signal: this.controller.signal,
      });
      // Runs once now, then whenever the row's width changes. Folding never changes the row's size, so it can't loop.
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this.list);
      // A web font that arrives late changes the text widths without resizing the row.
      document.fonts?.ready.then(() => this.isConnected && this.fit());
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    overflows() {
      return this.list.scrollWidth > this.list.clientWidth + 1;
    }

    fit() {
      if (this.hasAttribute('data-expanded')) return;
      this.removeAttribute('data-tight');
      this.setAttribute('data-measuring', '');
      for (const level of this.levels) level.hidden = false;
      if (this.more) this.more.hidden = true;

      if (this.more && this.levels.length > 0 && this.overflows()) {
        this.more.hidden = false;
        for (const level of this.levels) {
          level.hidden = true;
          if (!this.overflows()) break;
        }
      }

      // The floor for the tight state: 6em of the name (or all of it, if shorter), plus the separator before it.
      const em = parseFloat(getComputedStyle(this.list).fontSize) || 16;
      const separator = this.current.parentElement.offsetWidth - this.current.offsetWidth;
      this.style.setProperty('--crumb-floor', `${Math.min(this.current.scrollWidth, 6 * em) + separator}px`);
      const tight = this.overflows();
      this.removeAttribute('data-measuring');
      this.toggleAttribute('data-tight', tight);
    }

    expand() {
      const firstHidden = this.levels.find((level) => level.hidden);
      this.setAttribute('data-expanded', '');
      this.removeAttribute('data-tight');
      for (const level of this.levels) level.hidden = false;
      if (this.more) this.more.hidden = true;
      firstHidden?.querySelector('a')?.focus();
    }
  }

  customElements.define('breadcrumb-trail', BreadcrumbTrail);
}
